import React, { useState, useMemo, useEffect } from 'react';
import { Save, MapPin } from 'lucide-react';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { ManageRoutesFilters } from '../../components/delivery-management/ManageRoutesFilters';
import { RouteSummaryStats } from '../../components/delivery-management/RouteSummaryStats';
import { RouteJobsTable } from '../../components/delivery-management/RouteJobsTable';
import { useGetDeliveryJobsQuery, useBulkRescheduleDeliveryJobsMutation, useGetUserProfilesQuery } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { validateDeliveryDate } from '../../utils/deliveryDateValidation';
import { buildDeliveryEngineerFilterOptions } from '../../utils/palletJobFields';
import type { RouteSummary } from '../../types/deliveryManagement';

export const ManageRoutes: React.FC = () => {
  // Filter state
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0] // Today
  );
  const [selectedEngineerId, setSelectedEngineerId] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Local order state for drag-and-drop
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [originalOrder, setOriginalOrder] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Mutation and toast
  const [bulkReschedule, { isLoading: isSaving }] = useBulkRescheduleDeliveryJobsMutation();
  const toast = useToast();

  // Fetch delivery users for dropdown
  const { data: userProfiles } = useGetUserProfilesQuery({ 
    page: 1,
    page_size: 100,
    ordering: 'user__username'
  });

  const deliveryUsers = useMemo(
    () => buildDeliveryEngineerFilterOptions(userProfiles),
    [userProfiles]
  );

  // Fetch delivery jobs when loaded
  const { data: jobsData, isLoading, isError, error, refetch } = useGetDeliveryJobsQuery(
    {
      potential_delivery_date__date: selectedDate,
      delivery_user: selectedEngineerId || undefined,
      ordering: 'potential_delivery_date,-created_at',
    },
    { skip: !isLoaded || !selectedDate || !selectedEngineerId }
  );

  const jobs = jobsData?.results || [];

  // Update local order when jobs change
  useEffect(() => {
    if (jobs.length > 0) {
      const newOrder = jobs.map((job: any) => job.id);
      // Only update if the order actually changed
      setLocalOrder(prevOrder => {
        if (prevOrder.length !== newOrder.length) return newOrder;
        if (prevOrder.some((id, idx) => id !== newOrder[idx])) return newOrder;
        return prevOrder;
      });
      setOriginalOrder(newOrder);
      setHasUnsavedChanges(false);
    } else if (localOrder.length > 0) {
      setLocalOrder([]);
      setOriginalOrder([]);
      setHasUnsavedChanges(false);
    }
  }, [jobs.length, jobs.map((j: any) => j.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle order changes from drag-and-drop
  const handleOrderChange = (newOrder: string[]) => {
    setLocalOrder(newOrder);
    const changed = JSON.stringify(newOrder) !== JSON.stringify(originalOrder);
    setHasUnsavedChanges(changed);
  };

  // Handle save order (always POST when routes loaded — idempotent reorder / slot refresh)
  const handleSaveOrder = async () => {
    if (!selectedDate || jobs.length === 0 || localOrder.length === 0) {
      return;
    }

    const validationErrors: string[] = [];
    for (const job of jobs) {
      const validationError = validateDeliveryDate({
        potentialDeliveryDate: selectedDate,
        manufactureDate: job.manufacture_date,
        glassArrivalDate: job.glass_arrival_date,
        jobStatus: job.status,
        productionStatus: job.production_status
      });

      if (validationError) {
        validationErrors.push(`Job #${job.website_job_number || job.id}: ${validationError}`);
      }
    }
    
    if (validationErrors.length > 0) {
      toast.error(
        'Validation Failed',
        `Cannot save route due to validation errors:\n\n${validationErrors.join('\n')}`
      );
      return;
    }

    try {
      const result = await bulkReschedule({
        job_ids: localOrder,
        date: selectedDate,
      }).unwrap();

      toast.success('Route Saved', `Successfully saved route order! Updated ${result.updated_count} jobs.`);
      setHasUnsavedChanges(false);
      setOriginalOrder(localOrder);
      
      if (result.failed_count > 0) {
        toast.warning('Partial Update', `${result.failed_count} jobs failed to update. Check console for details.`);
        console.warn('Failed jobs:', result.failed_jobs);
      }
    } catch (error: any) {
      console.error('Failed to save route order:', error);
      toast.error('Save Failed', error?.data?.detail || 'Failed to save route order. Please try again.');
    }
  };

  // Calculate summary stats
  const summary: RouteSummary = useMemo(() => {
    const jobCount = jobs.length;
    const totalAmount = jobs.reduce((sum: number, job: any) => {
      return sum + parseFloat(job.total_amount || '0');
    }, 0);
    const totalQuantity = jobs.reduce((sum: number, job: any) => {
      return sum + (job.total_items || 0);
    }, 0);

    return { jobCount, totalAmount, totalQuantity };
  }, [jobs]);

  const handleLoadRoutes = () => {
    setIsLoaded(true);
  };

  // Handle job updated (date changed)
  const handleJobUpdated = () => {
    refetch(); // Job will be removed from list since date no longer matches filter
  };

  // Generate Google Maps route URL from current job order
  const generateGoogleMapsUrl = () => {
    // Get jobs in current local order
    const jobsMap = new Map(jobs.map((job: any) => [job.id, job]));
    const orderedJobs = localOrder.map(id => jobsMap.get(id)).filter(Boolean);
    
    // Extract postcodes and format them (replace spaces with +)
    const jobPostcodes = orderedJobs
      .map((job: any) => job.delivery_postcode?.replace(/\s+/g, '+'))
      .filter(Boolean);
    
    // Always start and end with DA1 4QX (home base)
    const homePostcode = 'DA1+4QX';
    const allPostcodes = [homePostcode, ...jobPostcodes, homePostcode];
    
    // Build Google Maps directions URL
    return `https://www.google.co.uk/maps/dir/${allPostcodes.join('/')}`;
  };

  return (
    <>
      {/* Filters */}
      <ManageRoutesFilters
        selectedDate={selectedDate}
        selectedEngineerId={selectedEngineerId}
        onDateChange={setSelectedDate}
        onEngineerChange={setSelectedEngineerId}
        onLoadRoutes={handleLoadRoutes}
        isLoading={isLoading}
      />

      {/* Summary Stats */}
      <RouteSummaryStats 
        summary={summary} 
        isVisible={isLoaded && jobs.length > 0} 
      />

      {/* Action Buttons — always visible when routes are loaded */}
      {isLoaded && jobs.length > 0 && (
        <div className="mb-4 flex flex-wrap justify-start gap-3">
          <Button
            variant="primary"
            icon={<Save className="w-4 h-4" />}
            onClick={handleSaveOrder}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="secondary"
            icon={<MapPin className="w-4 h-4" />}
            onClick={() => {
              const url = generateGoogleMapsUrl();
              window.open(url, '_blank');
            }}
          >
            Open in Google Maps
          </Button>
        </div>
      )}

      {/* Main Content */}
      <ContentCard>
        {!isLoaded ? (
          <div className="p-6 text-center text-gray-500">
            <p>Select a date and engineer, then click "Load Routes" to view jobs.</p>
          </div>
        ) : isError ? (
          <div className="p-6 text-center text-red-600">
            <p className="font-semibold mb-2">Error Loading Routes</p>
            <p className="text-sm">{(error as any)?.data?.detail || 'Failed to fetch delivery jobs'}</p>
            <p className="text-xs text-gray-500 mt-2">Check browser console for details</p>
          </div>
        ) : isLoading ? (
          <div className="p-6 text-center text-gray-500">
            <p>Loading routes...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No jobs found for the selected date and engineer.</p>
            <p className="text-xs mt-2">Try selecting a different date or engineer.</p>
          </div>
        ) : (
          <RouteJobsTable 
            jobs={jobs} 
            localOrder={localOrder}
            onOrderChange={handleOrderChange}
            onJobUpdated={handleJobUpdated}
            deliveryUsers={deliveryUsers}
            selectedEngineerId={selectedEngineerId}
          />
        )}
      </ContentCard>
    </>
  );
};
